import { type RefObject, useEffect, useRef } from "react";

import { prefersReducedMotion } from "../ui";

/** Logo palette: post-it yellow, rocket-trail cyan, flame orange, helmet ice, brand blue, paper. */
const COLORS = ["#fdce67", "#66d6e0", "#f9924b", "#bbd6e1", "#4f98d9", "#fffdf8"];
const PIECES = 170;
const GRAVITY = 0.22;
const DRAG = 0.985;

type Piece = {
	x: number;
	y: number;
	vx: number;
	vy: number;
	width: number;
	height: number;
	rotation: number;
	spin: number;
	flip: number;
	flipSpeed: number;
	color: string;
};

function createPiece(x: number, y: number): Piece {
	const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.9;
	const speed = 7 + Math.random() * 9;
	return {
		x,
		y,
		vx: Math.cos(angle) * speed,
		vy: Math.sin(angle) * speed,
		width: 6 + Math.random() * 6,
		height: 9 + Math.random() * 8,
		rotation: Math.random() * Math.PI * 2,
		spin: (Math.random() - 0.5) * 0.3,
		flip: Math.random() * Math.PI * 2,
		flipSpeed: 0.08 + Math.random() * 0.12,
		color: COLORS[Math.floor(Math.random() * COLORS.length)] ?? "#fdce67",
	};
}

/**
 * A single burst of paper confetti from the centre of `origin`, after `delay` ms. Skipped entirely
 * for readers who prefer reduced motion.
 */
export function Confetti({ origin, delay = 0 }: { origin: RefObject<HTMLElement | null>; delay?: number }) {
	const canvasRef = useRef<HTMLCanvasElement>(null);

	useEffect(() => {
		const canvas = canvasRef.current;
		const context = canvas?.getContext("2d");
		if (!canvas || !context || prefersReducedMotion()) {
			return;
		}

		const resize = () => {
			const ratio = Math.min(window.devicePixelRatio || 1, 2);
			canvas.width = window.innerWidth * ratio;
			canvas.height = window.innerHeight * ratio;
			context.setTransform(ratio, 0, 0, ratio, 0, 0);
		};
		resize();
		window.addEventListener("resize", resize);

		let pieces: Piece[] = [];
		let frame = 0;
		let last = 0;

		const tick = (now: number) => {
			// Normalised to 60 fps, so the burst looks the same on 120 Hz displays.
			const step = last ? Math.min((now - last) / (1000 / 60), 3) : 1;
			last = now;

			context.clearRect(0, 0, window.innerWidth, window.innerHeight);
			pieces = pieces.filter((piece) => piece.y < window.innerHeight + 40);

			for (const piece of pieces) {
				piece.vx *= DRAG ** step;
				piece.vy = piece.vy * DRAG ** step + GRAVITY * step;
				piece.x += piece.vx * step;
				piece.y += piece.vy * step;
				piece.rotation += piece.spin * step;
				piece.flip += piece.flipSpeed * step;

				context.save();
				context.translate(piece.x, piece.y);
				context.rotate(piece.rotation);
				context.scale(1, Math.cos(piece.flip));
				context.fillStyle = piece.color;
				context.fillRect(-piece.width / 2, -piece.height / 2, piece.width, piece.height);
				context.restore();
			}

			if (pieces.length > 0) {
				frame = requestAnimationFrame(tick);
			}
		};

		const timer = window.setTimeout(() => {
			const rect = origin.current?.getBoundingClientRect();
			const x = rect ? rect.left + rect.width / 2 : window.innerWidth / 2;
			const y = rect ? rect.top + rect.height / 2 : window.innerHeight / 3;
			pieces = Array.from({ length: PIECES }, () => createPiece(x, y));
			frame = requestAnimationFrame(tick);
		}, delay);

		return () => {
			window.clearTimeout(timer);
			cancelAnimationFrame(frame);
			window.removeEventListener("resize", resize);
		};
	}, [origin, delay]);

	return <canvas ref={canvasRef} className="pointer-events-none fixed inset-0 z-50 size-full" aria-hidden="true" />;
}
